import uuid from 'react-native-uuid';

import { ListMessagesDocument } from '@graphql/generated';

import type { ApolloCache } from '@apollo/client';

type PublishedMessageLike = {
  chatId?: string | null;
  clientId?: string | null;
  publishedAt?: string | null;
  __typename?: string;
};

// TA-10162: a sent message's local echo comes from listDraftMessages, and
// removeProcessedMessage drops it as soon as the create/update mutation
// completes — but the confirmed message only used to enter the listMessages
// connection when the async messageUpdate subscription event arrived
// (a Sidekiq job on the backend). In that gap the message vanished from the
// thread, then reappeared: the reported send flicker. Inserting the mutation
// response into the connection BEFORE the draft echo is removed closes the
// gap; the later subscription event is a no-op thanks to the identity check
// here and in the subscription handler.
export const insertPublishedMessageEdge = (
  cache: ApolloCache<unknown>,
  message: PublishedMessageLike | null | undefined
): boolean => {
  // Unpublished (draft) messages keep their existing rendering path via
  // listDraftMessages/listDraftDocuments — only published sends flicker.
  if (!message?.chatId || !message.publishedAt) return false;

  let inserted = false;
  cache.updateQuery(
    {
      query: ListMessagesDocument,
      // keyArgs for listMessages are [chatId, includeDraft, includePublished];
      // `first` is not part of the field key, so this matches the open chat's
      // connection (same variables shape the messageUpdate handler uses).
      variables: { chatId: message.chatId },
    },
    (data) => {
      if (!data?.listMessages) return undefined;
      const identity = cache.identify(message as never);
      if (!identity) return undefined;
      const edges = data.listMessages.edges || [];
      if (
        edges.some(
          (edge: { node?: unknown } | null) =>
            edge?.node && cache.identify(edge.node as never) === identity
        )
      )
        return undefined;

      inserted = true;
      return {
        listMessages: {
          ...data.listMessages,
          edges: edges.concat({
            cursor: uuid.v4().toString(),
            node: message,
          }),
        },
      };
    }
  );
  return inserted;
};
